import { useCallback, useState } from 'react';
import CompareLookDetailModal from './CompareLookDetailModal';
import LooksCompareSlider from './LooksCompareSlider';

function detailItemForSlot(resolved, originalSrc) {
  if (!resolved) return null;
  if (resolved.id === 'original') {
    return {
      id: 'original',
      label: 'Original',
      image: originalSrc,
      detailTitle: 'Original photo',
      detailLines: ['Your captured photo before any try-on.'],
    };
  }
  return resolved;
}

function CompareSlotHint({ slot, pickSlot, label, item, originalSrc, onOpenDetail, onPickSlotChange }) {
  const detailTarget = detailItemForSlot(item, originalSrc);
  return (
    <div className="looks-compare-board__slot-row">
      <button
        type="button"
        className={`looks-compare-board__slot-hint${pickSlot === slot ? ' is-picking' : ''}`}
        onClick={() => onPickSlotChange?.(slot)}
      >
        {slot === 'left' ? 'Left' : 'Right'}: {label || 'Select'}
      </button>
      {detailTarget ? (
        <button
          type="button"
          className="looks-compare-board__detail-cta"
          onClick={() => onOpenDetail(detailTarget)}
        >
          Details
        </button>
      ) : null}
    </div>
  );
}

/**
 * Compare board: vertical look history + Side-by-side or Slider mode.
 * Either pane/source can be Original or any stored applied look.
 */
export default function LooksCompareBoard({
  originalSrc,
  looks = [],
  leftId,
  rightId,
  pickSlot = 'right',
  onPickSlotChange,
  onSelectLook,
  mode = 'side',
  onModeChange,
  sliderPosition = 50,
  onSliderPositionChange,
  onDownloadLook,
  className = '',
}) {
  const [detailItem, setDetailItem] = useState(null);
  const openDetail = useCallback((item) => {
    if (item) setDetailItem(item);
  }, []);

  const byId = new Map(looks.map((item) => [String(item.id), item]));
  const resolve = (id) => {
    if (id === 'original') return { id: 'original', label: 'Original', image: originalSrc };
    return byId.get(String(id)) || null;
  };
  const left = resolve(leftId);
  const right = resolve(rightId);

  const railItems = [
    { id: 'original', label: 'Original', image: originalSrc, source: 'original' },
    ...looks,
  ];

  return (
    <>
      <div
        className={`looks-compare-board${mode === 'slider' ? ' looks-compare-board--slider' : ''}${
          className ? ` ${className}` : ''
        }`}
      >
        <div className="looks-compare-board__rail" role="listbox" aria-label="Looks to compare">
          {railItems.map((item) => {
            const id = String(item.id);
            const isLeft = String(leftId) === id;
            const isRight = String(rightId) === id;
            const detailPayload =
              id === 'original'
                ? detailItemForSlot({ id: 'original', label: 'Original', image: originalSrc }, originalSrc)
                : item;
            return (
              <div
                key={id}
                className={`looks-compare-board__thumb${isLeft || isRight ? ' is-selected' : ''}${
                  isLeft ? ' is-left' : ''
                }${isRight ? ' is-right' : ''}`}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={isLeft || isRight}
                  className="looks-compare-board__thumb-select"
                  onClick={() => onSelectLook?.(id)}
                  title={item.label}
                >
                  <img src={item.image} alt="" className="looks-compare-board__thumb-img" />
                  {isLeft || isRight ? (
                    <span className="looks-compare-board__badge">{isLeft ? 'L' : 'R'}</span>
                  ) : null}
                </button>
                <span className="looks-compare-board__thumb-label">{item.label}</span>
                <div className="looks-compare-board__thumb-actions">
                  <button
                    type="button"
                    className="looks-compare-board__detail-cta looks-compare-board__detail-cta--rail"
                    onClick={() => openDetail(detailPayload)}
                  >
                    Details
                  </button>
                  {onDownloadLook && item.image ? (
                    <button
                      type="button"
                      className="looks-compare-board__detail-cta looks-compare-board__detail-cta--rail looks-compare-board__download-cta--rail"
                      onClick={() => onDownloadLook(detailPayload)}
                      title={`Download ${item.label || 'look'}`}
                    >
                      QR
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="looks-compare-board__main">
          <div className="looks-compare-board__modes" role="tablist" aria-label="Compare mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'side'}
              className={`looks-compare-board__mode${mode === 'side' ? ' is-active' : ''}`}
              onClick={() => onModeChange?.('side')}
            >
              Side by side
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'slider'}
              className={`looks-compare-board__mode${mode === 'slider' ? ' is-active' : ''}`}
              onClick={() => onModeChange?.('slider')}
            >
              Slider
            </button>
          </div>

          {mode === 'slider' ? (
            <div className="looks-compare-board__slider-wrap">
              {left?.image && right?.image ? (
                <LooksCompareSlider
                  beforeSrc={left.image}
                  afterSrc={right.image}
                  position={sliderPosition}
                  onPositionChange={onSliderPositionChange}
                  className="looks-compare-board__slider"
                />
              ) : (
                <span className="looks-compare-board__empty">Pick two looks to compare</span>
              )}
              <div className="looks-compare-board__slot-hints">
                <CompareSlotHint
                  slot="left"
                  pickSlot={pickSlot}
                  label={left?.label}
                  item={left}
                  originalSrc={originalSrc}
                  onOpenDetail={openDetail}
                  onPickSlotChange={onPickSlotChange}
                />
                <CompareSlotHint
                  slot="right"
                  pickSlot={pickSlot}
                  label={right?.label}
                  item={right}
                  originalSrc={originalSrc}
                  onOpenDetail={openDetail}
                  onPickSlotChange={onPickSlotChange}
                />
              </div>
            </div>
          ) : (
            <div className="looks-compare-board__side-stack">
              <div className="looks-compare-board__slot-hints looks-compare-board__slot-hints--panes">
                <CompareSlotHint
                  slot="left"
                  pickSlot={pickSlot}
                  label={left?.label}
                  item={left}
                  originalSrc={originalSrc}
                  onOpenDetail={openDetail}
                  onPickSlotChange={onPickSlotChange}
                />
                <CompareSlotHint
                  slot="right"
                  pickSlot={pickSlot}
                  label={right?.label}
                  item={right}
                  originalSrc={originalSrc}
                  onOpenDetail={openDetail}
                  onPickSlotChange={onPickSlotChange}
                />
              </div>
              <div className="looks-compare-board__panes">
                <button
                  type="button"
                  className={`looks-compare-board__pane${pickSlot === 'left' ? ' is-picking' : ''}`}
                  onClick={() => onPickSlotChange?.('left')}
                  aria-pressed={pickSlot === 'left'}
                  aria-label="Left compare slot"
                >
                  <span className="looks-compare-board__pane-label">
                    {pickSlot === 'left' ? 'L · ' : ''}{left?.label || 'Select'}
                  </span>
                  {left?.image ? (
                    <img src={left.image} alt={left.label || 'Left compare'} className="looks-compare-board__img" />
                  ) : (
                    <span className="looks-compare-board__empty">Tap a look</span>
                  )}
                </button>
                <button
                  type="button"
                  className={`looks-compare-board__pane${pickSlot === 'right' ? ' is-picking' : ''}`}
                  onClick={() => onPickSlotChange?.('right')}
                  aria-pressed={pickSlot === 'right'}
                  aria-label="Right compare slot"
                >
                  <span className="looks-compare-board__pane-label">
                    {pickSlot === 'right' ? 'R · ' : ''}{right?.label || 'Select'}
                  </span>
                  {right?.image ? (
                    <img src={right.image} alt={right.label || 'Right compare'} className="looks-compare-board__img" />
                  ) : (
                    <span className="looks-compare-board__empty">Tap a look</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CompareLookDetailModal open={Boolean(detailItem)} item={detailItem} onClose={() => setDetailItem(null)} />
    </>
  );
}
